import React, { useMemo, useState, useEffect } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ProductViewDrawer from "../products/product-view-drawer";
import { mapSellerProductToDrawerDetail } from "../products/seller-product-mapper";
import { ApprovalHeader } from "./approval-header";
import { ApprovalFilterBar } from "./approval-filter-bar";
import { ApprovalTable } from "./approval-table";
import { ApprovalGrid } from "./approval-grid";
import { ApprovalPagination } from "./approval-pagination";
import {
  APPROVAL_API_STATUS_VALUES,
  APPROVAL_TAB_API_STATUS,
  mapProductByStatusItemToApprovalRow,
} from "./approval-mapper";
import {
  filterApprovalProducts,
  paginateSlice,
  totalPagesFor,
} from "./approval-utils";
import { GET_ORDER_STATS, GET_PRODUCTS_BY_STATUS } from "../../../../Utils/Queries";

const PAGE_SIZE = 10;

const PAGE_VARS = { page: null, limit: null };

const [ST_CREATED, ST_REJECTED, ST_APPROVED, ST_OUT_OF_STOCK] =
  APPROVAL_API_STATUS_VALUES;

export default function SellerProductApprovals() {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusMenu, setStatusMenu] = useState("");
  const [categoryMenu, setCategoryMenu] = useState("");
  const [page, setPage] = useState(1);
  const [drawerRow, setDrawerRow] = useState(null);

  const skipMulti = activeTab !== "all";

  const { data: statsData, loading: statsLoading } = useQuery(GET_ORDER_STATS, {
    fetchPolicy: "cache-and-network",
  });

  const qAllCreated = useQuery(GET_PRODUCTS_BY_STATUS, {
    variables: { status: ST_CREATED, ...PAGE_VARS },
    skip: skipMulti,
    fetchPolicy: "network-only",
  });
  const qAllRejected = useQuery(GET_PRODUCTS_BY_STATUS, {
    variables: { status: ST_REJECTED, ...PAGE_VARS },
    skip: skipMulti,
    fetchPolicy: "network-only",
  });
  const qAllApproved = useQuery(GET_PRODUCTS_BY_STATUS, {
    variables: { status: ST_APPROVED, ...PAGE_VARS },
    skip: skipMulti,
    fetchPolicy: "network-only",
  });
  const qAllOutOfStock = useQuery(GET_PRODUCTS_BY_STATUS, {
    variables: { status: ST_OUT_OF_STOCK, ...PAGE_VARS },
    skip: skipMulti,
    fetchPolicy: "network-only",
  });

  const multiResults = [
    qAllCreated,
    qAllRejected,
    qAllApproved,
    qAllOutOfStock,
  ];

  const singleStatus =
    APPROVAL_TAB_API_STATUS[activeTab] ?? APPROVAL_TAB_API_STATUS.pending;

  const singleQuery = useQuery(GET_PRODUCTS_BY_STATUS, {
    variables: { status: singleStatus, ...PAGE_VARS },
    skip: activeTab === "all",
    fetchPolicy: "network-only",
  });

  const productsLoading =
    activeTab === "all"
      ? multiResults.some((r) => r.loading)
      : singleQuery.loading;

  const productsError =
    activeTab === "all"
      ? multiResults.find((r) => r.error)?.error
      : singleQuery.error;

  const mappedRows = useMemo(() => {
    const seen = new Set();
    const rawItems = [];

    if (activeTab === "all") {
      for (const r of multiResults) {
        const items = r.data?.getProductsByStatus?.items;
        if (!Array.isArray(items)) continue;
        for (const p of items) {
          const pid = p?._id;
          if (!pid || seen.has(pid)) continue;
          seen.add(pid);
          rawItems.push(p);
        }
      }
    } else {
      const items = singleQuery.data?.getProductsByStatus?.items;
      if (Array.isArray(items)) {
        for (const p of items) {
          if (p?._id) rawItems.push(p);
        }
      }
    }

    return rawItems.map(mapProductByStatusItemToApprovalRow);
  }, [
    activeTab,
    singleQuery.data,
    qAllCreated.data,
    qAllRejected.data,
    qAllApproved.data,
    qAllOutOfStock.data,
  ]);

  const orderStats = statsData?.getOrderStats ?? null;

  useEffect(() => {
    if (activeTab !== "all") setStatusMenu("");
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search, statusMenu, categoryMenu]);

  const filtered = useMemo(
    () =>
      filterApprovalProducts(mappedRows, {
        tab: activeTab,
        search,
        statusMenu,
        category: categoryMenu,
      }),
    [mappedRows, activeTab, search, statusMenu, categoryMenu]
  );

  const totalPages = totalPagesFor(filtered.length, PAGE_SIZE);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => paginateSlice(filtered, safePage, PAGE_SIZE),
    [filtered, safePage]
  );

  const drawerDetail = drawerRow?.raw
    ? mapSellerProductToDrawerDetail(drawerRow.raw)
    : null;

  const handleView = (product) => setDrawerRow(product);
  const handleEdit = (product) =>
    navigate(`/seller/product/edit/${product.id}`);

  return (
    <div className="pb-12 pt-4 md:pt-8">
      <ApprovalHeader
        onExport={() => {}}
        orderStats={orderStats}
        orderStatsLoading={statsLoading}
      />

      <div className="min-w-0 rounded-[14px] border border-[#EFEFEF] bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] sm:p-5 md:p-6 lg:p-8">
        <ApprovalFilterBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          search={search}
          onSearchChange={setSearch}
          statusMenu={statusMenu}
          onStatusMenuChange={setStatusMenu}
          categoryMenu={categoryMenu}
          onCategoryMenuChange={setCategoryMenu}
          statusMenuDisabled={activeTab !== "all"}
        />

        {productsError ? (
          <Typography className="mt-6 text-center text-[14px] text-[#DC2626]">
            {productsError?.graphQLErrors?.[0]?.message ||
              productsError?.message ||
              "Could not load products."}
          </Typography>
        ) : null}

        <div className="relative mt-5 md:mt-8">
          {productsLoading && !mappedRows.length ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-10">
              <CircularProgress size={32} sx={{ color: "#2E1834" }} />
              <p className="text-[13px] text-[#6B7280]">Loading products…</p>
            </div>
          ) : (
            <>
              <div className="md:hidden">
                <ApprovalGrid
                  rows={pageRows}
                  onViewProduct={handleView}
                  onEditProduct={handleEdit}
                />
              </div>
              <div className="hidden overflow-x-auto md:block">
                <ApprovalTable
                  rows={pageRows}
                  onViewProduct={handleView}
                  onEditProduct={handleEdit}
                />
              </div>
            </>
          )}
        </div>

        <ApprovalPagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>

      <ProductViewDrawer
        open={drawerRow != null}
        productId={null}
        detail={drawerDetail}
        enableDelete
        onClose={() => setDrawerRow(null)}
        onProductDeleted={() => {
          setDrawerRow(null);
          client.refetchQueries({ include: [GET_PRODUCTS_BY_STATUS] });
        }}
      />
    </div>
  );
}
